<script>
  import Button from '@components/Button.svelte';
  import Input from '@components/Input.svelte';
  import Modal from '@components/Modal.svelte';
  import Auth from '@utils/auth';
  import Notification from '@utils/notification';
  import GroupAjax from '@ajax/group';
  import RequestAccessAjax from '@ajax/request_access';
  import MobileHeader from '@app-components/mobile/Header.svelte';
  import { getCurrentUserStore } from '@stores/current_user';
  import { onMount } from 'svelte';

  export let slug;
  export let mobile;
  export let params;

  let currentUser = getCurrentUserStore('default');

  let group = { id: `${params.username_or_id}|${slug}`, slug };
  let showGuestModal = false;
  let requestAccess;
  let loading = true;
  let passwordAccess = false;
  let showPasswordModal = false;
  let passwordInput;
  let guestAccessInput;

  let loadingSendGuestRequest = false;
  let loadingPasswordRequest = false;

  let handleRequestAccess = function () {
    if (currentUser && currentUser.api_token) {
      requestInviteFromCurrentUser();
    } else {
      requestInviteFromGuest();
    }
  };

  let showPasswordModalAction = function () {
    showPasswordModal = true;
  };

  let hidePasswordModalAction = function () {
    showPasswordModal = false;
  };

  let handlePasswordSubmit = async function () {
    loadingPasswordRequest = true;

    try {
      let resp = await GroupAjax.passwordAccess(group, {
        password: passwordInput.value,
      });

      if (resp && resp.token) {
        await Auth.load(currentUser, resp.token);
      }

      location.reload();
    } catch (err) {
      if (err && err.error) {
        Notification.send({
          text: err.error,
        });
      }
    }

    loadingPasswordRequest = false;
  };

  let requestInviteFromCurrentUser = async function () {
    try {
      let resp = await RequestAccessAjax.create(group);
      requestAccess = resp;
    } catch {}
  };

  let requestInviteFromGuest = function () {
    showGuestModal = true;
  };

  let handleGuestSubmit = async function () {
    loadingSendGuestRequest = true;

    try {
      let resp = await RequestAccessAjax.create(group, {
        email: guestAccessInput.value,
      });

      Notification.send({ text: 'Request sent to list owner' });
      requestAccess = resp;
      showGuestModal = false;
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }
    }

    loadingSendGuestRequest = false;
  };

  let loadPasswordAccess = async function () {
    let resp = await GroupAjax.passwordAccess(group, {
      withErrorHandling: false,
    });
    passwordAccess = resp.password_access;
  };

  let loadRequestAccess = async function () {
    loading = true;

    try {
      let resp = await RequestAccessAjax.getForUser(group, {
        withErrorHandling: false,
      });
      requestAccess = resp;
    } catch {}

    loading = false;
  };
  onMount(() => {
    loadRequestAccess();
    loadPasswordAccess();
  });
</script>

<style style="text/scss" lang="scss">
  .group-unauthorized {
    margin-top: 40px;
    background: #ffffff;
    box-shadow: 0px 22px 100px rgba(0, 0, 0, 0.0938593);
    border-radius: 25px;
    display: flex;
    width: 480px;
    height: 420px;
    margin-left: auto;
    margin-right: auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    &.mobile {
      position: absolute !important;
      bottom: 30px !important;
      left: 30px !important;
      right: 30px !important;
      width: unset !important;
    }
  }

  #enter-password {
    color: #8e71ff !important;

    &:hover {
      color: #7054dc !important;
    }
  }
</style>

<svelte:head>
  <meta name="robots" content="noindex" />
</svelte:head>

<div class="svelte-modal">
  <Modal
    mobile={mobile}
    on:close={() => {
      showGuestModal = false;
    }}
    show={showGuestModal}>
    <div slot="header">Request Access</div>

    <div slot="content">
      <form on:submit|preventDefault|stopPropagation={handleGuestSubmit}>
        <div style="margin-bottom: 26px">
          <Input bind:inputEl={guestAccessInput} placeholder="Enter your email address" focus={true} />
        </div>

        <div style="text-align: center">
          <Button
            loading={loadingSendGuestRequest}
            text="Send Request"
            style="margin-top: 0px; width: 230px;"
            on:click={handleGuestSubmit} />
        </div>
      </form>
    </div>
  </Modal>

  <Modal mobile={mobile} on:close={hidePasswordModalAction} show={showPasswordModal}>
    <div slot="header">Enter password to access</div>

    <div slot="content">
      <form on:submit|preventDefault|stopPropagation={handlePasswordSubmit}>
        <Input bind:inputEl={passwordInput} />

        <div style="text-align: center; margin-top: 26px;">
          <Button
            loading={loadingPasswordRequest}
            text="View List"
            style="margin-top: 0px; width: 230px;"
            on:click={handlePasswordSubmit} />
        </div>
      </form>
    </div>
  </Modal>
</div>

{#if mobile}
  <MobileHeader currentUser={currentUser} options={['profile', 'switch-accounts', 'signout']} />
{/if}

{#if slug && !loading}
  <div class="group-unauthorized" style={passwordAccess ? 'height: 470px' : ''} class:mobile={mobile}>
    <div style="margin-left: 14px">
      <svg width="77" height="74" viewBox="0 0 77 74" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M40.7996 15.1C40.7789 8.14983 35.1498 2.52076 28.1996
          2.5V2.5C21.2494 2.52076 15.6204 8.14983 15.5996 15.1V29.8"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
        <rect
          x="3"
          y="32.6501"
          width="50.4"
          height="39"
          rx="12"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
        <path
          d="M28.2004 55.3V61.6"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M28.2004 55.3C31.6798 55.3 34.5004 52.4793 34.5004 49C34.5004
          45.5206 31.6798 42.7 28.2004 42.7C24.721 42.7 21.9004 45.5206 21.9004
          49C21.9004 52.4793 24.721 55.3 28.2004 55.3Z"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
        <path
          d="M52.1772 12.5918L57.832 5.36635"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
        <path
          d="M69.4555 29.198L60.2803 29.2057"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
        <path
          d="M61.7282 18.4864L70.3847 14.2643"
          stroke="#E8E8E8"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
    </div>
    <div
      style="margin-top: 38px; font-weight: 500; font-size: 25px; line-height:
      36px;">
      This list is Private.
    </div>
    {#if requestAccess}
      <div
        style="width: 216px; font-style: normal; font-weight: normal; font-size:
        15px; line-height: 25px; text-align: center; margin-top: 30px; opacity:
        0.5">
        You have requested access to this list.
      </div>
    {:else}
      <div
        style="width: 216px; font-style: normal; font-weight: normal; font-size:
        15px; line-height: 25px; text-align: center; margin-top: 15px; opacity:
        0.5">
        In order to view content please request access below
      </div>
      <Button on:click={handleRequestAccess} style="margin-top: 43px; width: 230px" text="Request Access" />

      {#if passwordAccess}
        <div style="margin-top: 25px; opacity: 0.5; font-weight: 500">or</div>
        <a on:click|preventDefault={showPasswordModalAction} href="#" style="margin-top: 28px;" id="enter-password">
          Enter password
        </a>
      {/if}
    {/if}
  </div>
{/if}
